<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import minusIcon from '@fortawesome/fontawesome-pro/svgs/solid/minus.svg?raw';
import plusIcon from '@fortawesome/fontawesome-pro/svgs/solid/plus.svg?raw';
import type { CommerceLineItem } from '../types/commerce';
import { useCartStore } from '../stores/cart';

const props = defineProps<{
    item: CommerceLineItem;
}>();

const { t } = useI18n({ useScope: 'global' });
const cart = useCartStore();
const pendingQty = ref<number | null>(null);

watch(() => props.item.qty, () => {
    pendingQty.value = null;
});

const currentQty = computed(() => pendingQty.value ?? props.item.qty);

function updateQty(qty: number): void {
    pendingQty.value = qty;
    cart.updateLineItemQty(props.item.id, qty)
        .finally(() => {
            pendingQty.value = null;
        });
}

function handleQtyChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    if (value === 0) {
        cart.removeLineItem(props.item.id);
        return;
    }
    updateQty(value);
}
</script>

<template>
    <li class="cart-item">
        <div v-if="item.product?.previewImage" class="cart-item__image">
            <img :src="item.product.previewImage.url" :alt="item.product.previewImage.alt" :width="item.product.previewImage.width" :height="item.product.previewImage.height" />
        </div>
        <div class="cart-item__content">
            <div class="cart-item__header">
                <h3 class="cart-item__title">
                    <a class="cart-item__product-link" v-if="item.product?.url" :href="item.product.url">{{ item.description }}</a>
                    <span v-else>{{ item.description }}</span>
                </h3>
            </div>
            <div class="cart-item__price">
                <template v-if="item.onPromotion">
                    <span class="cart-item__price-sale">{{ item.salePriceAsCurrency }}</span>
                    <del class="cart-item__price-original">{{ item.priceAsCurrency }}</del>
                </template>
                <span v-else>{{ item.priceAsCurrency }}</span>
            </div>
            <div class="cart-item__controls">
                <div class="cart-item__stepper">
                    <button
                        type="button"
                        class="cart-item__stepper-btn"
                        :disabled="currentQty <= 0"
                        :aria-label="t('cart.decrement')"
                        @click="updateQty(currentQty - 1)"
                    >
                        <span aria-hidden="true" v-html="minusIcon" />
                    </button>
                    <input
                        type="number"
                        class="cart-item__stepper-input"
                        :value="currentQty"
                        :aria-label="t('cart.quantity')"
                        @change="handleQtyChange"
                    />
                    <button
                        type="button"
                        class="cart-item__stepper-btn"
                        :aria-label="t('cart.increment')"
                        @click="updateQty(currentQty + 1)"
                    >
                        <span aria-hidden="true" v-html="plusIcon" />
                    </button>
                </div>
                <button
                    type="button"
                    class="cart-item__remove"
                    @click="cart.removeLineItem(item.id)"
                >
                    {{ t('cart.removeItem') }}
                </button>
            </div>
        </div>
    </li>
</template>
